import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactElement, type ReactNode } from "react";

import { apiFetchBlob } from "@/lib/api";
import type { ControlledDocument } from "@/lib/documents";
import { cn } from "@/lib/utils";

import {
  importStepCadMeshes,
  stepCadPreviewTooLarge,
  stepCadPreviewTooLargeMessage,
} from "./stepCadPreview";

export function StepCadDocumentPreview(props: {
  document: ControlledDocument;
  fillAvailableHeight?: boolean;
  headerActions?: ReactNode;
}): ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const blobQuery = useQuery({
    queryKey: ["documents", "preview-blob", props.document.id],
    queryFn: ({ signal }) =>
      apiFetchBlob(`/api/documents/${encodeURIComponent(props.document.id)}/preview`, { signal }),
    staleTime: 240_000,
    gcTime: 240_000,
  });

  useEffect(() => {
    const host = hostRef.current;
    const blob = blobQuery.data?.blob;
    if (host == null || blob == null) return undefined;
    let cancelled = false;
    let release: (() => void) | null = null;
    setReady(false);
    setError(null);
    void (async () => {
      try {
        if (stepCadPreviewTooLarge(blob.size)) {
          throw new Error(stepCadPreviewTooLargeMessage());
        }
        const bytes = new Uint8Array(await blob.arrayBuffer());
        const meshes = await importStepCadMeshes(bytes);
        if (cancelled) return;
        const view = await import("./stepCadPreviewView");
        if (cancelled) return;
        release = view.mountStepCadPreview(host, meshes);
        if (cancelled) {
          release();
          release = null;
          return;
        }
        setReady(true);
      } catch (caught) {
        if (cancelled) return;
        setError(caught instanceof Error ? caught.message : "Could not preview this STEP file.");
      }
    })();
    return () => {
      cancelled = true;
      release?.();
    };
  }, [blobQuery.data]);

  const fill = props.fillAvailableHeight === true;

  return (
    <div
      className={cn(
        "rounded-lg border border-slate-200 bg-slate-50",
        fill && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Preview</h3>
        <div className="flex shrink-0 items-center gap-2">
          <p className="text-xs text-slate-500">Drag to rotate. Scroll to zoom.</p>
          {props.headerActions ? <div className="flex shrink-0 items-center gap-1">{props.headerActions}</div> : null}
        </div>
      </div>
      <div className={cn("relative", fill ? "min-h-0 flex-1" : "min-h-[28rem]")}>
        <div ref={hostRef} className="absolute inset-0" />
        {blobQuery.isPending ? (
          <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-slate-500">
            Loading 3D preview…
          </p>
        ) : null}
        {blobQuery.isError ? (
          <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-red-700">
            {(blobQuery.error as Error).message ?? "Could not load preview."}
          </p>
        ) : null}
        {error != null ? (
          <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-red-700">
            {error}
          </p>
        ) : null}
        {blobQuery.isSuccess && error == null && !ready ? (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-slate-500">
            Reading STEP model…
          </p>
        ) : null}
      </div>
    </div>
  );
}
